import type { DataItem } from '@ricoh/ria.client.core/types/component/data.type';

import { Url } from '@ricoh/ria.client.core/utils/es/url';

import type { IntegrationAIWorkFlowType, IntegrationProvider, IntegrationType, IntegrationTypeAI } from '~/common/model/api/integration';

/** Integration providers. */
export const INTEGRATION_PROVIDERS = (() => {
  const importProviders = ['Microsoft', 'Outlook', 'Sftp'] satisfies IntegrationProvider[];
  const routingProviders = ['Microsoft', 'AwsS3Bucket', 'Sftp', 'Docuware'] satisfies IntegrationProvider[];
  const aiProviders = ['NatifAI', 'AzureAI'] satisfies IntegrationProvider[];
  const connectorProviders = [...aiProviders, 'Rcip'] satisfies IntegrationProvider[];

  return {
    import: importProviders,
    routing: routingProviders,
    ai: aiProviders,
    connector: connectorProviders,
  } as const;
})();

/** Integration types. */
export const INTEGRATION_TYPES: IntegrationType[] = ['microsoft', 'outlook', 'awsS3Bucket', 'sftp', 'natifAI', 'azureAI', 'rcip', 'docuware'];

/** Integration Types AI. */
export const INTEGRATION_TYPES_AI: IntegrationTypeAI[] = ['natifAI', 'azureAI'];

/** Integration Provider name. */
export const INTEGRATION_PROVIDER_NAME: Record<IntegrationType, string> = {
  microsoft: 'Microsoft OneDrive',
  outlook: 'Outlook',
  awsS3Bucket: 'AWS S3 Bucket',
  sftp: 'SFTP',
  natifAI: 'Natif AI',
  azureAI: 'Azure AI',
  rcip: 'RCIP',
  docuware: 'Docuware',
} as const;

/** Integration Redirect URL. */
export const INTEGRATION_REDIRECT_URL = {
  docuware: new Url()
    .resolve(import.meta.env.APP_SERVER_BASE_WEB)
    .resolve(import.meta.env.APP_SERVER_PATH_API)
    .resolve('integration/oauth2/docuware/delegate-callback')
    .toString(),
} satisfies Partial<Record<IntegrationType, string>>;

/** Integration Base URL example. */
export const INTEGRATION_BASE_URL_EXAMPLE = {
  natifAI: 'https://api.natif.ai',
  azureAI: 'https://example.cognitiveservices.azure.com',
  docuware: 'https://example.docuware.cloud',
  rcip: 'https://example.dwres.es/app-version/api/service-name/rcip',
} satisfies Partial<Record<IntegrationType, string>>;

/** AWS regions. */
export const AWS_REGIONS: DataItem[] = [
  { text: 'Africa (Cape Town)', value: 'af-south-1' },
  { text: 'Asia Pacific (Hong Kong)', value: 'ap-east-1' },
  { text: 'Asia Pacific (Tokyo)', value: 'ap-northeast-1' },
  { text: 'Asia Pacific (Seoul)', value: 'ap-northeast-2' },
  { text: 'Asia Pacific (Osaka)', value: 'ap-northeast-3' },
  { text: 'Asia Pacific (Mumbai)', value: 'ap-south-1' },
  { text: 'Asia Pacific (Hyderabad)', value: 'ap-south-2' },
  { text: 'Asia Pacific (Singapore)', value: 'ap-southeast-1' },
  { text: 'Asia Pacific (Sydney)', value: 'ap-southeast-2' },
  { text: 'Asia Pacific (Jakarta)', value: 'ap-southeast-3' },
  { text: 'Asia Pacific (Melbourne)', value: 'ap-southeast-4' },
  { text: 'Canada (Central)', value: 'ca-central-1' },
  { text: 'Canada West (Calgary)', value: 'ca-west-1' },
  { text: 'Europe (Frankfurt)', value: 'eu-central-1' },
  { text: 'Europe (Zurich)', value: 'eu-central-2' },
  { text: 'Europe (Stockholm)', value: 'eu-north-1' },
  { text: 'Europe (Milan)', value: 'eu-south-1' },
  { text: 'Europe (Spain)', value: 'eu-south-2' },
  { text: 'Europe (Ireland)', value: 'eu-west-1' },
  { text: 'Europe (London)', value: 'eu-west-2' },
  { text: 'Europe (Paris)', value: 'eu-west-3' },
  { text: 'Israel (Tel Aviv)', value: 'il-central-1' },
  { text: 'Middle East (UAE)', value: 'me-central-1' },
  { text: 'Middle East (Bahrain)', value: 'me-south-1' },
  { text: 'South America (Sao Paulo)', value: 'sa-east-1' },
  { text: 'US East (N. Virginia)', value: 'us-east-1' },
  { text: 'US East (Ohio)', value: 'us-east-2' },
  { text: 'US West (N. California)', value: 'us-west-1' },
  { text: 'US West (Oregon)', value: 'us-west-2' },
  { text: 'China (Beijing)', value: 'cn-north-1' },
  { text: 'China (Ningxia)', value: 'cn-northwest-1' },
  { text: 'AWS GovCloud (US-East)', value: 'us-gov-east-1' },
  { text: 'AWS GovCloud (US-West)', value: 'us-gov-west-1' },
  { text: 'US ISO East', value: 'us-iso-east-1' },
  { text: 'US ISO WEST', value: 'us-iso-west-1' },
  { text: 'US ISOB East (Ohio)', value: 'us-isob-east-1' },
];

/** Integration WorkFlow Types. */
export const INTEGRATION_WORK_FLOW_TYPES: Record<IntegrationTypeAI, IntegrationAIWorkFlowType[]> = {
  natifAI: ['ocr', 'separation', 'classification', 'extraction'],
  azureAI: ['ocr', 'separation', 'classification', 'extraction'],
};
